import { Button, Skeleton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { EditBudgetCapModal } from 'src/Components/Modals/EditBudgetCapModal';
import { useUpdateBudgetCap } from 'src/Hooks/Mutations/useUpdateBudgetCap';
import useBudgetTracking from 'src/Hooks/Queries/useBudgetTracking';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { BudgetTrackingRowDtoSectionEnum } from 'src/Services/API/generated';
import { getCategoryLabel } from 'src/Utils/expenseCategories';

import { CategoryDonut } from './CategoryDonut';
import styles from './HouseGaugeCard.module.scss';
import { Props } from './HouseGaugeCard.types';

const MAX_DONUT_SLICES = 4;

// Sauge and terracotta locked to the Home v2.1 palette (see TwoColumnCard).
const OVER_BUDGET_COLOR = '#a15737';
const UNDER_BUDGET_COLOR = '#4f6a55';
const CAP_MARK_COLOR = '#3c3c43';

// Ordered so the four expected top categories keep the same colours across visits.
const CATEGORY_COLORS = ['#4f6a55', '#8a6a2e', '#a15737', '#4e6f76', '#96a0aa'];
const OTHER_COLOR = '#c9c2b7';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);

const HouseGaugeCard = ({ finances }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const currentYear = new Date().getFullYear();
  const { data: tracking, isLoading: isTrackingLoading } = useBudgetTracking(currentYear);

  const spentTotal = tracking?.spentTotal ?? 0;
  const cap = finances.annualBudgetCap ?? 0;
  const hasBudgetCap = cap > 0;
  const remaining = cap - spentTotal;
  const isOverBudget = remaining < 0;

  const goToBudget = () => {
    navigate(`/asset/${assetId}/expenses?view=forecast`);
  };

  const [isBudgetModalOpen, budgetModal] = useDisclosure(false);
  const { mutate: updateBudgetCap, isLoading: isUpdatingBudgetCap } = useUpdateBudgetCap({
    onSuccess: budgetModal.close,
  });

  const handleSaveBudgetCap = (annualBudgetCap: number | null) => {
    if (!assetId) return;
    updateBudgetCap({ assetId, data: { annualBudgetCap } });
  };

  const slices = useMemo(() => {
    const rows = tracking?.rows ?? [];
    // Charges only: incomes and refunds must not appear in the spending donut.
    const chargeRows = rows.filter(row => row.section !== BudgetTrackingRowDtoSectionEnum.Income);
    const totals = new Map<string, number>();
    chargeRows.forEach(row => {
      const amount = row.actualAmount ?? 0;
      if (amount <= 0) return;
      totals.set(row.mainCategory, (totals.get(row.mainCategory) ?? 0) + amount);
    });
    const sorted = Array.from(totals.entries())
      .map(([mainCategory, value]) => ({ mainCategory, value }))
      .sort((a, b) => b.value - a.value);
    const top = sorted.slice(0, MAX_DONUT_SLICES);
    const restValue = sorted
      .slice(MAX_DONUT_SLICES)
      .reduce((total, entry) => total + entry.value, 0);
    const result = top.map((entry, index) => ({
      key: entry.mainCategory,
      label: getCategoryLabel(entry.mainCategory),
      value: entry.value,
      color: CATEGORY_COLORS[index] ?? OTHER_COLOR,
    }));
    if (restValue > 0) {
      result.push({
        key: 'OTHER',
        label: t('dashboard.houseGauge.other', 'Autres'),
        value: restValue,
        color: OTHER_COLOR,
      });
    }
    return result;
  }, [tracking, t]);

  const hasSlices = slices.length > 0;
  const donutTotal = slices.reduce((total, slice) => total + slice.value, 0);

  const summaryLabel = hasBudgetCap
    ? isOverBudget
      ? t(
          'dashboard.houseGauge.overBudget',
          '{{spent}} dépensés sur {{cap}} prévus — dépassement de {{over}}',
          {
            spent: formatCurrency(spentTotal),
            cap: formatCurrency(cap),
            over: formatCurrency(Math.abs(remaining)),
          }
        )
      : t(
          'dashboard.houseGauge.underBudget',
          '{{spent}} dépensés sur {{cap}} prévus — il reste {{left}}',
          {
            spent: formatCurrency(spentTotal),
            cap: formatCurrency(cap),
            left: formatCurrency(Math.max(remaining, 0)),
          }
        )
    : '';

  const capMarkPercent = hasBudgetCap
    ? Math.min(100, Math.max(0, (cap / Math.max(spentTotal, cap)) * 100))
    : 0;
  const fillPercent = hasBudgetCap
    ? Math.min(100, (spentTotal / Math.max(spentTotal, cap)) * 100)
    : 0;

  return (
    <>
      <div className={styles.card}>
        <h3 className={styles.title}>
          {t('dashboard.houseGauge.titleV2', 'Budget {{year}} de la maison', { year: currentYear })}
        </h3>

        {isTrackingLoading ? (
          <Skeleton height={140} radius="md" />
        ) : hasSlices ? (
          <div className={styles.donutRow}>
            <CategoryDonut
              slices={slices}
              centerLabel={formatCurrency(donutTotal)}
              size={112}
              ariaLabel={t(
                'dashboard.houseGauge.donutAria',
                'Répartition des dépenses par catégorie'
              )}
            />
            <ul className={styles.legend}>
              {slices.map(slice => (
                <li key={slice.key} className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ background: slice.color }} />
                  <span className={styles.legendLabel}>{slice.label}</span>
                  <span className={styles.legendValue}>{formatCurrency(slice.value)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className={styles.empty}>
            {t(
              'dashboard.houseGauge.noSpending',
              "Aucune dépense enregistrée pour l'instant sur {{year}}.",
              { year: currentYear }
            )}
          </p>
        )}

        {hasBudgetCap ? (
          <div className={styles.capBlock}>
            <div className={styles.capTrack} aria-hidden="true">
              <span
                className={styles.capFill}
                style={{
                  width: `${fillPercent}%`,
                  background: isOverBudget ? OVER_BUDGET_COLOR : UNDER_BUDGET_COLOR,
                }}
              />
              <span
                className={styles.capMark}
                style={{ left: `${capMarkPercent}%`, background: CAP_MARK_COLOR }}
              />
            </div>
            <p className={styles.capLabel}>{summaryLabel}</p>
          </div>
        ) : (
          !isTrackingLoading && (
            <Button variant="outline" size="sm" onClick={budgetModal.open}>
              {t('dashboard.houseGauge.prepareBudget', 'Préparer mon budget')}
            </Button>
          )
        )}

        <div className={styles.actions}>
          <Button variant="outline" size="md" onClick={goToBudget}>
            {t('dashboard.houseGauge.cta', 'Voir le budget')}
          </Button>
        </div>
      </div>

      <EditBudgetCapModal
        open={isBudgetModalOpen}
        onClose={budgetModal.close}
        currentBudgetCap={finances.annualBudgetCap}
        onSave={handleSaveBudgetCap}
        isLoading={isUpdatingBudgetCap}
      />
    </>
  );
};

export default HouseGaugeCard;
